/**
 * ConsultationHistoryPanel — Integration Hub
 * -----------------------------------------------------------------------------
 * Aba "Histórico de Consultas" do conector: lista paginada (server-side) das
 * consultas executadas, com filtros, detalhe do payload e acesso ao PDF.
 *
 * Acesso restrito a administradores do tenant (revalidado no banco).
 * O documento aparece sempre mascarado.
 *
 * @version 1.4.0
 * RELEASE: 2026-08-29 v1.4.0 - Ação confirmada para cancelar consultas ativas
 *          e novo estado terminal "Cancelada" no histórico e nos filtros.
 * RELEASE: 2026-08-29 v1.3.0 - Mostra a tentativa atual quando a consulta é
 *          assíncrona e o sistema ainda aguarda o retorno do provider.
 * RELEASE: 2026-08-29 v1.2.0 - Exibe consultas em andamento (fila/processando),
 *          com filtro próprio e atualização automática enquanto houver pendência.
 * RELEASE: 2026-08-29 v1.1.0 - Ações separadas por botão (PDF e JSON); preview
 *          do PDF padronizado via ImageLightbox (componente universal).
 * RELEASE: 2026-08-29 v1.0.0 - Criação inicial.
 */

import { useMemo, useState } from 'react';
import { Ban, Braces, FileText, Loader2, RotateCw, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConsultationResultDialog } from '@/components/data/ConsultationResultDialog';
import { ImageLightbox } from '@/components/tenant/chat/ImageLightbox';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  fetchConsultationPdfUrl,
  useCancelIntegrationConsultation,
  useIntegrationConsultation,
  useIntegrationConsultations,
} from '@/hooks/integrations/useIntegrationConsultations';
import type { ConsultationListItem } from '@/hooks/integrations/useIntegrationConsultations';
import type { IntegrationOperation } from '@/hooks/integrations/useIntegrationConnections';

/** Períodos pré-definidos do filtro */
const PERIODS = [
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: 'all', label: 'Todo o período' },
];

interface ConsultationHistoryPanelProps {
  providerSlug: string;
  /** Recursos do provider (para o filtro e para reexecutar) */
  operations: IntegrationOperation[];
  /** Abre o modal de nova consulta já preenchido */
  onRerun?: (operationCode: string) => void;
}

/** Formata data/hora no padrão brasileiro */
function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('pt-BR');
}

/** Converte centavos em moeda */
function formatCost(cents: number | null): string {
  if (cents === null || cents === undefined) return '—';
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function ConsultationHistoryPanel({
  providerSlug,
  operations,
  onRerun,
}: ConsultationHistoryPanelProps) {
  const [period, setPeriod] = useState('30');
  const [status, setStatus] = useState('all');
  const [operationCode, setOperationCode] = useState('all');
  const [documentInput, setDocumentInput] = useState('');
  const [documentFilter, setDocumentFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ConsultationListItem | null>(null);

  // Preview do PDF (padrão universal: ImageLightbox em modo 'document')
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);
  const [pdfPreview, setPdfPreview] = useState<{ url: string; title: string } | null>(null);

  /** Busca a URL assinada (5 min) e abre o preview interno padrão */
  const handleOpenPdf = async (consultationId: string, title: string) => {
    setPdfLoadingId(consultationId);
    const url = await fetchConsultationPdfUrl(consultationId);
    setPdfLoadingId(null);
    if (!url) {
      toast.error('Não foi possível abrir o PDF desta consulta');
      return;
    }
    setPdfPreview({ url, title });
  };

  const dateFrom = useMemo(() => {
    if (period === 'all') return null;
    const days = Number(period);
    return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  }, [period]);

  const { items, total, pageSize, isLoading, isFetching, refetch } =
    useIntegrationConsultations({
      providerSlug,
      operationCode: operationCode === 'all' ? null : operationCode,
      status: status === 'all' ? null : status,
      dateFrom,
      document: documentFilter || null,
      page,
    });

  const detail = useIntegrationConsultation(selectedId);
  const cancelConsultation = useCancelIntegrationConsultation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      {/* ─────────── Filtros ─────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={period}
          onValueChange={(value) => {
            setPeriod(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-8 w-[170px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-8 w-[140px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="queued">Na fila</SelectItem>
            <SelectItem value="processing">Processando</SelectItem>
            <SelectItem value="success">Sucesso</SelectItem>
            <SelectItem value="error">Erro</SelectItem>
            <SelectItem value="cancelled">Cancelada</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={operationCode}
          onValueChange={(value) => {
            setOperationCode(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-8 w-[220px] text-xs">
            <SelectValue placeholder="Recurso" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os recursos</SelectItem>
            {operations.map((op) => (
              <SelectItem key={op.code} value={op.code}>
                {op.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1">
          <Input
            value={documentInput}
            maxLength={20}
            placeholder="Documento consultado"
            className="h-8 w-[200px] text-xs"
            onChange={(e) => setDocumentInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setDocumentFilter(documentInput.trim());
                setPage(1);
              }
            }}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={() => {
              setDocumentFilter(documentInput.trim());
              setPage(1);
            }}
          >
            <Search className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Atualizar listagem manualmente (sem precisar recarregar a página) */}
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
          disabled={isFetching}
          title="Atualizar listagem"
          aria-label="Atualizar listagem"
          onClick={() => refetch()}
        >
          <RotateCw className={isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
          Atualizar
        </Button>
      </div>

      {/* ─────────── Tabela ─────────── */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Data</TableHead>
              <TableHead className="text-xs">Recurso</TableHead>
              <TableHead className="text-xs">Documento</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs">Usuário</TableHead>
              <TableHead className="text-xs">Custo</TableHead>
              <TableHead className="text-xs text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            )}

            {!isLoading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                  Nenhuma consulta registrada no período.
                </TableCell>
              </TableRow>
            )}

            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-xs">{formatDateTime(item.created_at)}</TableCell>
                <TableCell className="text-xs">
                  {item.operation_name || item.operation_code}
                </TableCell>
                <TableCell className="text-xs">{item.document_masked ?? '—'}</TableCell>
                <TableCell>
                  {/* Consultas em andamento aparecem imediatamente (fila assíncrona) */}
                  {item.status === 'queued' || item.status === 'processing' ? (
                    <Badge variant="outline" className="gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {item.status === 'queued'
                        ? 'Na fila'
                        : `Processando${item.attempts && item.attempts > 1 ? ` (${item.attempts})` : ''}`}
                    </Badge>
                  ) : (
                    <Badge
                      variant={item.status === 'success' || item.status === 'cancelled' ? 'secondary' : 'destructive'}
                    >
                      {item.status === 'success'
                        ? 'Sucesso'
                        : item.status === 'cancelled'
                          ? 'Cancelada'
                          : 'Erro'}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-xs">{item.user_name ?? '—'}</TableCell>
                <TableCell className="text-xs">{formatCost(item.cost_cents)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {/* Botão PDF — abre o preview interno padrão do sistema */}
                    {item.has_pdf && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        title="Visualizar PDF"
                        aria-label="Visualizar PDF"
                        disabled={pdfLoadingId === item.id}
                        onClick={() =>
                          handleOpenPdf(
                            item.id,
                            item.operation_name || item.operation_code,
                          )
                        }
                      >
                        {pdfLoadingId === item.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <FileText className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    )}

                    {/* Botão JSON — abre o modal com os dados retornados */}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      title="Ver dados retornados"
                      aria-label="Ver dados retornados"
                      onClick={() => setSelectedId(item.id)}
                    >
                      <Braces className="h-3.5 w-3.5" />
                    </Button>

                    {(item.status === 'queued' || item.status === 'processing') && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1 text-xs text-destructive hover:text-destructive"
                        disabled={cancelConsultation.isPending}
                        onClick={() => setCancelTarget(item)}
                      >
                        {cancelConsultation.isPending && cancelTarget?.id === item.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Ban className="h-3 w-3" />
                        )}
                        Cancelar
                      </Button>
                    )}

                    {onRerun && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1 text-xs"
                        onClick={() => onRerun(item.operation_code)}
                      >
                        <RotateCw className="h-3 w-3" />
                        Reexecutar
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* ─────────── Paginação ─────────── */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {total} consulta(s) · página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}

      {/* ─────────── Detalhe da consulta (JSON) ─────────── */}
      <ConsultationResultDialog
        open={Boolean(selectedId)}
        onOpenChange={(open) => !open && setSelectedId(null)}
        title={detail.data?.operation_name || detail.data?.operation_code || 'Consulta'}
        payload={
          detail.data?.payload_purged_at
            ? { aviso: 'Conteúdo expurgado pela política de retenção (90 dias).' }
            : detail.data?.response_payload
        }
        subtitle={
          detail.data
            ? `${formatDateTime(detail.data.created_at)} · ${detail.data.document_masked ?? 'sem documento'} · ${formatCost(detail.data.cost_cents)}`
            : undefined
        }
        errorMessage={detail.data?.error_message ?? null}
      />

      {/* Confirmação obrigatória para uma ação terminal e não reversível. */}
      <AlertDialog
        open={Boolean(cancelTarget)}
        onOpenChange={(open) => !open && !cancelConsultation.isPending && setCancelTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar processamento?</AlertDialogTitle>
            <AlertDialogDescription>
              O acompanhamento desta consulta será interrompido imediatamente e o fluxo vinculado
              não seguirá para os próximos nodes. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelConsultation.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cancelConsultation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (!cancelTarget) return;
                cancelConsultation.mutate(cancelTarget.id, {
                  onSuccess: () => setCancelTarget(null),
                });
              }}
            >
              {cancelConsultation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cancelar processamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─────────── Preview do PDF (componente universal) ─────────── */}
      <ImageLightbox
        open={Boolean(pdfPreview)}
        onClose={() => setPdfPreview(null)}
        mediaItems={
          pdfPreview
            ? [{ url: pdfPreview.url, type: 'document' as const, title: pdfPreview.title }]
            : []
        }
      />
    </div>
  );
}
